import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { SeoHead } from "@/components/public/SeoHead";
import { JsonLd } from "@/components/public/JsonLd";
import { ChevronLeft, Leaf, AlertTriangle, XCircle, Recycle, Shirt, Award, Sparkles, FileText } from "lucide-react";

const PALETTE = {
  GREEN: { dot: "bg-leaf", text: "text-leaf", border: "border-leaf", chip: "bg-leaf/10 text-leaf" },
  YELLOW: { dot: "bg-amber", text: "text-amber", border: "border-amber", chip: "bg-amber/10 text-amber" },
  RED: { dot: "bg-clay", text: "text-clay", border: "border-clay", chip: "bg-clay/10 text-clay" },
};
const ICON = { GREEN: Leaf, YELLOW: AlertTriangle, RED: XCircle };

export default function MaterialDetailPage() {
  const { slug } = useParams();
  const { data: m, isLoading, error } = useQuery({
    queryKey: ["material-detail", slug],
    queryFn: async () => (await api.get(`/public/materials/${slug}`)).data,
    retry: false,
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-muted_text">Loading…</div>;
  if (error || !m) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-14 text-center">
        <h1 className="font-serif text-4xl mb-4">Material not found</h1>
        <Link to="/materials-guide" className="text-forest underline">Back to materials guide</Link>
      </div>
    );
  }

  const palette = PALETTE[m.classification] || PALETTE.GREEN;
  const Icon = ICON[m.classification] || Leaf;

  const origin = typeof window !== "undefined" ? window.location.origin : "https://sustainablycurated.com";
  const materialUrl = `${origin}/materials/${slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      name: m.name,
      description: m.short_summary || m.full_description?.slice(0, 280),
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        name: "Sustainably Curated Materials Guide",
        url: origin + "/materials-guide",
      },
      url: materialUrl,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Materials Guide", item: origin + "/materials-guide" },
        { "@type": "ListItem", position: 3, name: m.name, item: materialUrl },
      ],
    },
  ];

  const quickFacts = [
    { label: "Classification", value: m.classification, accent: palette.text },
    m.biodegradability_notes && { label: "Biodegradability", value: m.biodegradability_notes },
    m.common_uses && { label: "Common uses", value: m.common_uses },
    m.better_alternatives && { label: "Better alternatives", value: m.better_alternatives },
  ].filter(Boolean);

  const sections = [
    m.classification_reason && { icon: Icon, title: `Why ${m.classification}`, body: m.classification_reason },
    m.sustainability_notes && { icon: Leaf, title: "Sustainability notes", body: m.sustainability_notes },
    m.biodegradability_notes && { icon: Recycle, title: "Biodegradability", body: m.biodegradability_notes },
    m.common_uses && { icon: Shirt, title: "Common uses in clothing", body: m.common_uses },
    m.certifications && { icon: Award, title: "Relevant certifications", body: m.certifications },
    m.better_alternatives && { icon: Sparkles, title: "Better alternatives", body: m.better_alternatives },
    m.source_notes && { icon: FileText, title: "Source notes", body: m.source_notes },
  ].filter(Boolean);

  return (
    <article className="bg-ivory" data-testid="material-detail-page">
      <SeoHead
        title={m.seo_title || `${m.name} (${m.classification}) — Material Guide — Sustainably Curated`}
        description={m.seo_description || m.short_summary || m.full_description?.slice(0, 160)}
        path={`/materials/${slug}`}
      />
      <JsonLd data={jsonLd} />

      <DetailHeader
        eyebrow="Material"
        title={m.name}
        subtitle={m.aliases?.length ? <><span className="text-xs uppercase tracking-wider text-muted_text mr-2">Also known as</span><span className="italic">{m.aliases.join(", ")}</span></> : null}
        lead={m.short_summary}
        badge={<ClassificationBadgeLarge classification={m.classification} palette={palette} />}
        accentLine={palette.dot}
        backLabel="All materials"
        backTo="/materials-guide"
      />

      <DetailBody
        sections={sections}
        body={m.full_description}
        quickFacts={quickFacts}
      />
    </article>
  );
}

function snippet(t, n) {
  if (!t) return "";
  return t.length > n ? t.slice(0, n).trim() + "…" : t;
}

function ClassificationBadgeLarge({ classification, palette }) {
  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 border ${palette.border} ${palette.text} font-medium tracking-wider text-xs uppercase rounded-full`} data-testid={`classification-badge-${classification}`}>
      <span className={`w-2 h-2 rounded-full ${palette.dot}`} />
      {classification}
    </div>
  );
}

// Shared header + body components ----------------------------------------

export function DetailHeader({ eyebrow, title, subtitle, lead, badge, backLabel, backTo, accentLine = "bg-forest" }) {
  return (
    <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8" data-testid="detail-header">
      {backTo && (
        <div className="mb-6">
          <Link to={backTo} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-forest hover:underline" data-testid="detail-back-to-list">
            <ChevronLeft className="w-4 h-4" /> {backLabel}
          </Link>
        </div>
      )}

      <div className={`w-12 h-[3px] ${accentLine} mb-6`} aria-hidden />

      <div className="grid md:grid-cols-[1fr_auto] gap-6 items-start md:items-center mb-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-leaf font-medium mb-3">{eyebrow}</p>
          <h1 className="font-serif text-4xl sm:text-5xl leading-[1.05] text-ink">{title}</h1>
        </div>
        {badge && <div className="shrink-0">{badge}</div>}
      </div>

      {subtitle && <p className="text-sm text-muted_text mb-4">{subtitle}</p>}
      {lead && <p className="font-serif text-xl sm:text-2xl text-ink/85 leading-snug max-w-3xl mt-4">{lead}</p>}
    </header>
  );
}

export function DetailBody({ sections, body, quickFacts, ctaPrimary, ctaSecondary }) {
  return (
    <>
      <div className="border-t border-line" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-[260px_1fr] gap-10">
          {/* Quick facts sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start space-y-6" data-testid="detail-quick-facts">
            {(quickFacts || []).map((q, i) => (
              <div key={q.label} className={i === 0 ? "" : "border-t border-line pt-4"}>
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted_text mb-1">{q.label}</p>
                <p className={`text-sm leading-snug ${q.accent || "text-ink"}`}>{q.value}</p>
              </div>
            ))}
            {(ctaPrimary || ctaSecondary) && (
              <div className="border-t border-line pt-4 space-y-3">
                {ctaPrimary}
                {ctaSecondary}
              </div>
            )}
          </aside>

          {/* Body */}
          <div className="space-y-10" data-testid="detail-body">
            {body && <p className="text-base text-ink/85 leading-relaxed whitespace-pre-line">{body}</p>}
            {(sections || []).map((s) => (
              <DetailSection key={s.title} icon={s.icon} title={s.title}>{s.body}</DetailSection>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function DetailSection({ icon: SectionIcon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        {SectionIcon && <SectionIcon className="w-4 h-4 text-forest" />}
        <h2 className="font-serif text-2xl leading-tight">{title}</h2>
      </div>
      <p className="text-base text-ink/85 leading-relaxed whitespace-pre-line">{children}</p>
    </section>
  );
}
